import { useEffect, useState } from "react";

export type GuidePhase = "onboarding" | "graduated" | "expert";

const USER_KEY = "paperchase-guide-user";

function readPersisted(): GuidePhase {
  try {
    const raw = localStorage.getItem(USER_KEY);
    if (!raw) return "onboarding";
    const parsed = JSON.parse(raw) as { expert?: boolean; graduated?: boolean };
    if (parsed.expert) return "expert";
    if (parsed.graduated) return "graduated";
  } catch {
    // ignore
  }
  return "onboarding";
}

/**
 * Returns the persisted guide phase from localStorage, decoupled from
 * `useGuideState`'s count-derived `allCoreComplete` shortcut.
 *
 * Used on routes (like /analyze) that can't cheaply assemble the real
 * ServerCounts but still need to know the user's true phase for things
 * like FeatureRef routing (data-guide-phase body attribute). Expand, which
 * has full counts, still uses `useGuideState.phase` directly.
 *
 * Listens for cross-window storage events + the in-app `guide:phase-change`
 * event (dispatched by the drawer footer toggle).
 */
export function usePersistedGuidePhase(): GuidePhase {
  const [phase, setPhase] = useState<GuidePhase>(readPersisted);

  useEffect(() => {
    function refresh() {
      setPhase(readPersisted());
    }
    window.addEventListener("guide:phase-change", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("guide:phase-change", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, []);

  return phase;
}
